import { memo, useState } from "react";
import { useParams } from "react-router-dom";

import { InviteMember } from "../InviteMember";

import { WorkspaceNameDialog } from "./WorkspaceNameDialog";
import { WorkspaceFallbackOwnerDialog } from "./WorkspaceFallbackOwnerDialog";
import { useWorkspaceColumns, useWorkspaceActions, useWorkspaceOperations } from "./hooks";
import { CreateWorkspaceModal } from "./CreateWorkspaceDialog";

import { useDataTableDeleteConfirm } from "@/hooks/useDataTableDeleteConfirm";
import { DataTable } from "@/components/ui/ag-grid-table";
import { Workspace } from "@/types/workspaces";
import { TableColumn } from "@/components/ui/TableV2";
import { useCurrentUser } from "@/queries/users";
import { useDebounce } from "@/hooks/debounce";

const Workspaces = memo(({ organizationId }: { organizationId: number }) => {
  const onRequestDeleteConfirm = useDataTableDeleteConfirm();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [isCreateWorkspaceDialogOpen, setIsCreateWorkspaceDialogOpen] = useState(false);
  const [fallbackOwnerWorkspace, setFallbackOwnerWorkspace] = useState<Workspace | null>(null);
  const { workspaceId } = useParams();
  const { data: user } = useCurrentUser();
  const {
    accessibleWorkspaces,
    isAddMemberDialogOpen,
    setIsAddMemberDialogOpen,
    isChangeNameDialogOpen,
    setIsChangeNameDialogOpen,
    selectedWorkspace,
    setSelectedWorkspace,
    handleDeleteWorkspace,
    handleEditWorkspaceName,
    handleSaveWorkspaceName,
    getFilteredWorkspaces,
    handleAddMemberToWorkspace,
    isLoadingWorkspaces,
    handleSetDefaultWorkspace,
  } = useWorkspaceOperations({
    organizationId,
    search: debouncedSearch,
    page: 1,
    perPage: 100,
  });

  const filteredWorkspaces = getFilteredWorkspaces();

  const columns = useWorkspaceColumns({
    accessibleWorkspaces,
    currentUser: user,
  });

  const { actions, contextMenuActions } = useWorkspaceActions({
    currentUser: user,
    accessibleWorkspaces,
    onEditName: handleEditWorkspaceName,
    onAddMember: handleAddMemberToWorkspace,
    onSetDefault: handleSetDefaultWorkspace,
    onSetFallbackOwner: setFallbackOwnerWorkspace,
    organizationId,
  });

  return (
    <div className="flex h-full flex-col">
      <WorkspaceNameDialog
        open={isChangeNameDialogOpen}
        onOpenChange={setIsChangeNameDialogOpen}
        workspace={selectedWorkspace}
        onWorkspaceChange={setSelectedWorkspace}
        onSave={handleSaveWorkspaceName}
      />
      <WorkspaceFallbackOwnerDialog
        open={fallbackOwnerWorkspace !== null}
        onOpenChange={open => {
          if (!open) {
            setFallbackOwnerWorkspace(null);
          }
        }}
        workspace={fallbackOwnerWorkspace}
      />
      <div className="flex-1 overflow-hidden">
        <DataTable
          contextMenuItems={contextMenuActions}
          data={filteredWorkspaces ?? []}
          columns={columns as TableColumn<Workspace>[]}
          isLoading={isLoadingWorkspaces}
          rowsDeletable
          getDeleteDisabled={workspace => !workspace?.admin_user_ids.includes(user?.id || 0)}
          action={
            <>
              <InviteMember
                open={isAddMemberDialogOpen}
                isWorkspacesTable
                setOpen={setIsAddMemberDialogOpen}
                workspace={selectedWorkspace}
                hasTrigger={false}
                organizationId={organizationId}
              />
              <CreateWorkspaceModal
                isOpen={isCreateWorkspaceDialogOpen}
                setOpen={setIsCreateWorkspaceDialogOpen}
                organizationId={organizationId}
                hasTrigger={false}
              />
            </>
          }
          actions={actions}
          onDelete={workspace => handleDeleteWorkspace(workspace!.id)}
          onRequestDeleteConfirm={onRequestDeleteConfirm}
          objectName="workspace"
          search={search}
          setSearch={setSearch}
          isHighlighted={workspace => workspace?.id === Number(workspaceId)}
          hasSearch
          searchPlaceholder="Search workspaces"
          emptyStateMessage="No workspaces found"
        />
      </div>
    </div>
  );
});

export default Workspaces;
